import { useState } from "react";
import {
  Stack,
  Text,
  TextInput,
  Button,
  Alert,
  Anchor,
  CopyButton,
  Code,
  Center,
  Box,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { QRCodeSVG } from "qrcode.react";

interface MfaSetupProps {
  /** otpauth:// URI for the authenticator app */
  totpSetupUri: string;
  onSubmit: (code: string) => void;
  isPending: boolean;
  error: Error | null;
  /** US-176 Phase 2 — the voluntary Settings-page enrollment flow reuses this
   * component but isn't mid-sign-in, so the default label would mislead. */
  submitLabel?: string;
}

/** Extracts the base32 secret from an otpauth URI for manual entry */
function extractSecret(uri: string): string {
  try {
    const secret = new URL(uri).searchParams.get("secret");
    return secret ?? uri;
  } catch {
    return uri;
  }
}

export function MfaSetup({
  totpSetupUri,
  onSubmit,
  isPending,
  error,
  submitLabel = "Verify and sign in",
}: MfaSetupProps) {
  const [code, setCode] = useState("");
  const secret = extractSecret(totpSetupUri);

  return (
    <Stack gap="md">
      <Text size="sm" fw={500}>
        Set up two-factor authentication
      </Text>
      <Text size="sm" c="dimmed">
        Scan this QR code with your authenticator app (Google Authenticator,
        Authy, Microsoft Authenticator, etc.), then enter the 6-digit code it
        shows.
      </Text>

      {/* Rendered locally as SVG — the otpauth URI carries the TOTP secret,
          so it must never be sent to a third-party QR-image service. White
          background + quiet-zone margin so it stays scannable in dark mode. */}
      <Center>
        <Box
          p="sm"
          bg="white"
          style={{ borderRadius: 8, lineHeight: 0 }}
          data-testid="totp-qr-code"
        >
          <QRCodeSVG
            value={totpSetupUri}
            size={184}
            level="M"
            marginSize={2}
            bgColor="#ffffff"
            fgColor="#000000"
            role="img"
            aria-label="QR code for setting up two-factor authentication"
          />
        </Box>
      </Center>

      <Stack gap="xs">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          Can&apos;t scan? Enter this setup key instead
        </Text>
        <Code block style={{ wordBreak: "break-all", fontSize: 13 }}>
          {secret}
        </Code>
        <CopyButton value={secret}>
          {({ copied, copy }) => (
            <Anchor component="button" size="xs" onClick={copy}>
              {copied ? "Copied!" : "Copy key"}
            </Anchor>
          )}
        </CopyButton>
        <Anchor href={totpSetupUri} size="xs" target="_blank" rel="noreferrer">
          Open in authenticator app
        </Anchor>
      </Stack>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          title="Invalid code"
        >
          {error.message ||
            "The code you entered is incorrect. Please try again."}
        </Alert>
      )}

      <TextInput
        label="Verification code"
        placeholder="000000"
        maxLength={6}
        inputMode="numeric"
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        autoFocus
      />

      <Button
        fullWidth
        size="md"
        loading={isPending}
        disabled={code.length !== 6}
        onClick={() => onSubmit(code)}
      >
        {submitLabel}
      </Button>
    </Stack>
  );
}
